import { useEffect } from 'react';
import { Button, Card } from '@strategy4kids/ui';
import type { CoachAdvice } from '@strategy4kids/ai-coach';
import type { GameSnapshot } from '@strategy4kids/protocol';
import { prefetchTts } from '../services/speech.js';
import { useProfileStore } from '../state/profile-store.js';
import { useOnlineStore } from '../state/online-store.js';
import { formatClock, formatMoveTime } from '../lib/clock.js';
import { CoachPanel } from './CoachPanel.js';
import { useT } from '../i18n/useT.js';

export function OnlineGamePanel({ onExit }: { onExit: () => void }) {
  const { t } = useT('games');
  const { t: tCommon } = useT('common');
  const game = useOnlineStore((s) => s.game);
  const phase = useOnlineStore((s) => s.phase);
  const coach = useOnlineStore((s) => s.coach);
  const coachLoading = useOnlineStore((s) => s.coachLoading);
  const coachOffNote = useOnlineStore((s) => s.coachOffNote);
  const submitPending = useOnlineStore((s) => s.submitPending);
  const resign = useOnlineStore((s) => s.resign);
  const askCoach = useOnlineStore((s) => s.askCoach);
  const clock = useOnlineStore((s) => s.clock);
  const moves = useOnlineStore((s) => s.moves);
  const opponentOnline = useOnlineStore((s) => s.opponentOnline);
  const liveStatus = useOnlineStore((s) => s.liveStatus);
  const spectating = game?.spectating === true;

  const advice: CoachAdvice[] | null = coach?.map((m) => ({
    san: m.san,
    uci: '',
    scoreCp: 0,
    line: [...m.line],
    explanation: m.explanation,
    speakable: m.speakable,
  })) ?? null;

  useEffect(() => {
    const token = useProfileStore.getState().profile?.token;
    if (!token || !coach) return;
    for (const m of coach) if (m.speakable) prefetchTts(m.speakable, { token });
  }, [coach]);

  return (
    <aside className="flex w-full flex-col gap-4 lg:w-72">
      <Card title="Game">
        <p className="text-lg font-extrabold text-ink" data-testid="turn-banner">
          {spectating ? t('spectating') : turnText(game, t)}
        </p>
        <p className="text-sm font-semibold text-ink/60" data-testid="opponent-label">
          {game?.opponent === 'ai' ? tCommon('vs-robot') : tCommon('vs-friend')}
          {game?.rated ? ` · ${tCommon('rated')}` : ` · ${tCommon('casual')}`}
        </p>
        {game?.opponent === 'human' && clock && (
          <div className="mt-2 flex justify-between rounded-xl bg-ink/5 p-2 font-mono text-xl font-black text-ink" data-testid="clock-row">
            <span data-testid="clock-w">{formatClock(clock.w)}</span>
            <span data-testid="clock-b">{formatClock(clock.b)}</span>
          </div>
        )}
        {game?.opponent === 'human' && !spectating && (
          <p className="mt-1 text-sm font-semibold text-ink/60" data-testid="opponent-state">
            {opponentOnline ? t('opponent-online') : t('waiting-for-opponent')}
          </p>
        )}
        {game?.opponent === 'human' && liveStatus === 'reconnecting' && (
          <p className="mt-1 text-sm font-bold text-accent" data-testid="live-status">
            {t('reconnecting')}
          </p>
        )}
        {moves.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1" data-testid="move-list">
            {moves.slice(-6).map((m) => (
              <li key={m.index} className="flex items-center justify-between text-sm font-semibold text-ink/80" data-testid="move-item">
                <span>
                  {m.index}. {m.san}
                </span>
                <span className="font-mono text-xs text-ink/50">{formatMoveTime(m.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
        {!spectating && (
        <Button
          data-testid="resign-button"
          disabled={phase !== 'playing' || submitPending}
          onClick={() => void resign()}
          className="min-h-[48px] mt-2 w-full !py-2 !px-3 text-sm"
        >
          {t('resign')}
        </Button>
        )}
      </Card>
      {!spectating && (
        <CoachPanel advice={advice} loading={coachLoading} onAsk={() => void askCoach()} disabled={phase !== 'playing'} />
      )}
      {coachOffNote && (
        <p className="text-sm font-semibold text-ink/60" data-testid="coach-off-note">
          {t('coach-stays-off')}
        </p>
      )}
      <Button variant="ghost" data-testid="online-exit" onClick={onExit}>
        {t('back-to-lobby')}
      </Button>
    </aside>
  );
}

function turnText(game: GameSnapshot | null, t: (key: string) => string): string {
  if (!game) return t('connecting');
  if (game.status === 'completed') return t('game-over');
  return game.turn === game.side ? t('your-turn') : t('opponent-thinking');
}
